import { Table as SCTable } from '@keytrade/components-table';
import React from 'react';
import styled from 'styled-components';
import { graphql } from 'gatsby';
import { config } from '@keytrade/functions';
import { mediaQuery } from '@/utils/helpers';

const { list: colors } = config.colors;

export type TableType = {
  table: {
    raw: string;
  };
  isAlternateDesign?: boolean;
  dark?: boolean;
};

const extractCellValue = (cell: any): string | undefined => {
  return cell?.content?.[0]?.content?.[0]?.value;
};

const createColumnKey = (label: string): string => {
  return label.trim().toLowerCase().replace(/ /g, '_');
};

const getTableCols = (firstRow) => {
  let tableCols = [];

  const { nodeType, content } = firstRow || {};

  if (nodeType === 'table-row' && !!content) {
    tableCols = firstRow.content.map((cell: any) => {
      const value = extractCellValue(cell);

      if (cell.nodeType === 'table-header-cell' && extractCellValue(cell)) {
        return {
          label: value,
          key: createColumnKey(value),
        };
      }

      return { label: 'Unknown', key: 'unknown' };
    });
  }

  return tableCols;
};

const getRowsData = (rows, tableCols) => {
  const rowsData = [];

  for (let i = 1; i < rows.length; i++) {
    if (rows[i].nodeType === 'table-row' && rows[i].content) {
      const rowData: any = {};

      rows[i].content.forEach((cell: any, index: number) => {
        if (cell.nodeType === 'table-cell' && extractCellValue(cell)) {
          const key = tableCols[index]
            ? tableCols[index].key
            : `unknown_${index}`;

          rowData[key] = cell.content[0].content[0].value;
        }
      });

      rowsData.push(rowData);
    }
  }

  return rowsData;
};

const getTableData = (rawData) => {
  let tableCols: any[] = [];
  let tableData: any[] = [];

  if (!rawData?.content?.[0]?.content?.length) {
    return { tableData, tableCols };
  }

  const tableNode = rawData.content.find((item: any) => {
    return item.nodeType === 'table';
  });

  if (!tableNode?.content?.length) {
    return { tableData, tableCols };
  }

  const rows = tableNode.content;
  const firstRow = rows?.[0];

  tableCols = getTableCols(firstRow);
  tableData = getRowsData(rows, tableCols);

  return { tableData, tableCols };
};

const Table: React.FC<TableType> = (props) => {
  const rawData = JSON.parse(props.table.raw);
  const { tableData, tableCols } = getTableData(rawData);

  if (!tableData?.length || !tableCols?.length) {
    return null;
  }

  return (
    <TableWrapper isAlternateDesign dark={!!props?.dark}>
      <SCTable
        columns={tableCols}
        data={tableData}
        styledComponentsV4
        isLight
        isAlternateDesign
      />
    </TableWrapper>
  );
};

export default Table;

const TableWrapper = styled.div<TableType>`
  ${({ isAlternateDesign }) =>
    isAlternateDesign &&
    `
      margin-top: 24px !important;
      margin-bottom: 24px !important;
      ${mediaQuery.md(`
        margin-top: 32px !important;
        margin-bottom: 32px !important;
      `)}
      border-radius: 2.4rem;
      box-shadow: 0px 2px 11px 0px #0000000D, 0px 0px 23px 0px #0000000D;
      tbody tr:last-of-type td {
        border-bottom: none !important;
      }
      td, th { 
        padding: 2.1rem 3.2rem;
        text-align: center;
        &:first-of-type {
          text-align: left;
        }
      }
    `}
  ${({ isAlternateDesign }) =>
    !isAlternateDesign &&
    `
      tbody tr:last-of-type td:first-of-type {
        border-bottom-left-radius: .4rem !important;
      }
      tbody tr:last-of-type td:last-of-type {
        border-bottom-right-radius: .4rem !important;
      }
    `}
  ${({ dark }) =>
    !dark &&
    `
      tbody tr td, thead tr th {
        background-color: ${colors.White};
      }
      tbody tr:first-of-type td{
        border-top: 0.1rem solid ${dark ? colors.BlueDark : colors.GreyWhite};
      }
    `}
`;

export const tableQuery = graphql`
  fragment TableRichTextFragment on ContentfulTable {
    contentful_id
    __typename
    table {
      raw
    }
  }
`;
